import { Cubit } from "blac-next";
import type { PDFDocumentLoadingTask } from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";
import type { RenderTask } from "pdfjs-dist/types/src/display/api";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { TranslationKey } from "src/types/translationKey";

export interface PDFViewerState {
  isOpen: boolean;
  blobUrl: string | null;
  filename: string | null;
  currentPage: number;
  numPages: number;
  loading: boolean;
  isRendering: boolean;
  error: TranslationKey | null;
  zoom: number;
  minZoom: number;
  maxZoom: number;
}

export interface ScrollPosition {
  scrollLeft: number;
  scrollTop: number;
  containerWidth: number;
  canvasWidth: number;
}

export default class PDFViewerBloc extends Cubit<PDFViewerState> {
  pdf: PDFDocumentProxy | null = null;
  canvasElement: HTMLCanvasElement | null = null;
  loadingTask: PDFDocumentLoadingTask | null = null;
  currentRenderTask: RenderTask | null = null;
  containerElement: HTMLDivElement | null = null;

  constructor() {
    super({
      isOpen: false,
      blobUrl: null,
      filename: null,
      currentPage: 1,
      numPages: 0,
      loading: false,
      isRendering: false,
      error: null,
      zoom: 1.0,
      minZoom: 0.5,
      maxZoom: 3.0
    });
  }

  public readonly open = (blobUrl: string, filename: string): void => {
    this.emit({
      isOpen: true,
      blobUrl,
      filename,
      error: null,
      currentPage: 1,
      numPages: 0,
      loading: true,
      isRendering: false,
      zoom: 1.0,
      minZoom: 0.5,
      maxZoom: 3.0
    });
  };

  public readonly close = (): void => {
    if (this.currentRenderTask) {
      try {
        this.currentRenderTask.cancel();
      } catch {
        // Ignore cancellation errors
      }
      this.currentRenderTask = null;
    }

    if (this.state.blobUrl) {
      try {
        URL.revokeObjectURL(this.state.blobUrl);
      } catch {
        // Ignore revocation errors
      }
    }

    if (this.loadingTask) {
      void this.loadingTask.destroy();
      this.loadingTask = null;
      this.pdf = null;
    }

    this.emit({
      isOpen: false,
      blobUrl: null,
      filename: null,
      currentPage: 1,
      numPages: 0,
      loading: false,
      isRendering: false,
      error: null,
      zoom: 1.0,
      minZoom: 0.5,
      maxZoom: 3.0
    });
  };

  setCanvasEl = (el: HTMLCanvasElement | null) => {
    if (!el) {
      return;
    }
    this.canvasElement = el;
  };

  setContainerEl = (el: HTMLDivElement | null) => {
    if (!el) {
      return;
    }
    this.containerElement = el;
  };

  openDocument = async (loadingTask: PDFDocumentLoadingTask) => {
    this.loadingTask = loadingTask;
    loadingTask.promise
      .then((loadedPdf) => {
        this.pdf = loadedPdf;
        this.patch({
          numPages: loadedPdf.numPages,
          currentPage: 1,
          loading: false,
          error: null
        });

        // Double setTimeout ensures React completes state updates and re-renders
        // before we attempt to render the PDF to the canvas element
        setTimeout(() => {
          setTimeout(() => {
            void this.renderPage();
          });
        }, 0);
      })
      .catch((err: unknown) => {
        if (!this.state.isOpen) {
          return;
        }

        if (
          err instanceof Error &&
          err.message.includes("Worker was destroyed")
        ) {
          return;
        }

        reportErrorSentry(err);
        this.patch({
          error: "error.pdfLoadFailed",
          loading: false
        });
      });
  };

  renderPage = async () => {
    if (!this.pdf || !this.canvasElement) {
      return;
    }

    if (this.currentRenderTask) {
      this.currentRenderTask.cancel();
      this.currentRenderTask = null;
    }

    const canvas = this.canvasElement;

    this.patch({ isRendering: true });

    try {
      const page = await this.pdf.getPage(this.state.currentPage);
      const context = canvas.getContext("2d");
      if (!context) {
        this.patch({ isRendering: false });
        return;
      }

      const maxInitialCancasWidth = 1024;

      const container = canvas.parentElement;
      const containerWidth = Math.min(
        container?.clientWidth ?? 800,
        maxInitialCancasWidth
      );
      const containerPaddingLeft = container
        ? parseFloat(getComputedStyle(container).paddingLeft)
        : 0;
      const containerPaddingRight = container
        ? parseFloat(getComputedStyle(container).paddingRight)
        : 0;
      const availableWidth =
        containerWidth - containerPaddingLeft - containerPaddingRight;

      const viewport = page.getViewport({ scale: 1 });
      const baseScale = availableWidth / viewport.width;

      const scale = baseScale * this.state.zoom;
      const scaledViewport = page.getViewport({ scale });

      // Use devicePixelRatio for crisp rendering on high-DPI displays
      const pixelRatio = window.devicePixelRatio || 1;

      const useWidthCanvas = scaledViewport.width;

      canvas.width = useWidthCanvas * pixelRatio;
      canvas.height = scaledViewport.height * pixelRatio;

      canvas.style.width = `${useWidthCanvas}px`;
      canvas.style.height = `${scaledViewport.height}px`;

      context.scale(pixelRatio, pixelRatio);

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
        canvas
      };

      this.currentRenderTask = page.render(renderContext);
      await this.currentRenderTask.promise;
      this.currentRenderTask = null;
      this.patch({ isRendering: false });
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "RenderingCancelledException") {
        return;
      }

      if (
        err instanceof Error &&
        err.message.includes("Worker was destroyed")
      ) {
        this.patch({ isRendering: false });
        return;
      }

      if (!this.state.isOpen) {
        this.patch({ isRendering: false });
        return;
      }

      reportErrorSentry(err);
      this.patch({
        error: "error.pdfRenderFailed",
        isRendering: false
      });
    }
  };

  handlePrevPage = () => {
    if (this.state.isRendering) {
      return;
    }
    this.patch({
      currentPage: Math.max(this.state.currentPage - 1, 1),
      zoom: 1.0
    });
    void this.renderPage();
  };

  handleNextPage = () => {
    if (this.state.isRendering) {
      return;
    }
    this.patch({
      currentPage: Math.min(this.state.currentPage + 1, this.state.numPages),
      zoom: 1.0
    });
    void this.renderPage();
  };

  private adjustScrollForZoom = (oldZoom: number, newZoom: number) => {
    if (!this.containerElement || !this.canvasElement) {
      return;
    }

    const container = this.containerElement;
    const oldScrollLeft = container.scrollLeft;
    const oldScrollTop = container.scrollTop;
    const containerCenterX = container.clientWidth / 2;
    const centerXInCanvas = oldScrollLeft + containerCenterX;

    requestAnimationFrame(() => {
      const zoomRatio = newZoom / oldZoom;
      const newCenterXInCanvas = centerXInCanvas * zoomRatio;
      const newScrollLeft = newCenterXInCanvas - containerCenterX;
      const newScrollTop = oldScrollTop * zoomRatio;

      container.scrollLeft = Math.max(0, newScrollLeft);
      container.scrollTop = Math.max(0, newScrollTop);
    });
  };

  zoomIn = () => {
    const oldZoom = this.state.zoom;
    const newZoom = Math.min(this.state.zoom + 0.25, this.state.maxZoom);
    this.patch({ zoom: newZoom });
    void this.renderPage().then(() => {
      this.adjustScrollForZoom(oldZoom, newZoom);
    });
  };

  zoomOut = () => {
    const oldZoom = this.state.zoom;
    const newZoom = Math.max(this.state.zoom - 0.25, this.state.minZoom);
    this.patch({ zoom: newZoom });
    void this.renderPage().then(() => {
      this.adjustScrollForZoom(oldZoom, newZoom);
    });
  };

  setZoom = (zoom: number) => {
    const oldZoom = this.state.zoom;
    const clampedZoom = Math.max(
      this.state.minZoom,
      Math.min(zoom, this.state.maxZoom)
    );
    this.patch({ zoom: clampedZoom });
    void this.renderPage().then(() => {
      this.adjustScrollForZoom(oldZoom, clampedZoom);
    });
  };

  resetZoom = () => {
    this.patch({ zoom: 1.0 });
    void this.renderPage();
  };
}
